import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';  
import { FaGithub, FaArrowLeft, FaPlay, FaChevronLeft, FaChevronRight, FaExternalLinkAlt, FaSearchPlus } from 'react-icons/fa';  // Add new icons
import { projects } from '../data/projectsData';
import { useState, useEffect, useRef } from 'react';  // Add useEffect and useRef import

const DetailContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 480px 1fr;
  gap: 2.5rem;
  margin-bottom: 2.5rem;
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: 220px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: clamp(1.6rem, 6vw, 2.5rem);
  margin-bottom: 1rem;
  color: #2c3e50;
  overflow-wrap: break-word;
`;

const Technologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  span {
    background: #f0f4f8;
    color: #2c3e50;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
  }
`;

const Description = styled.div`
  margin-bottom: 2rem;
  line-height: 1.8;
  color: #4a5568;
  font-size: 1.1rem;
`;

const Screenshots = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);  // Changed to fixed 3 columns
  gap: 2.5rem;
  margin-top: 2rem;
  padding: 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MobileScreenshotsContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 2rem 0;
  padding: 1rem 0;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const MobileScreenshots = styled.div`
  display: flex;
  gap: 2rem;
  overflow-x: auto;
  padding: 1rem;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;

  @media (max-width: 768px) {
    gap: 0;
    padding: 0.5rem;
  }

  & > * {
    scroll-snap-align: center;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
    @media (max-width: 768px) {
      display: none;
    }
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    
    &:hover {
      background: #555;
    }
  }
`;

const ScreenshotCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background-color: ${props => props.isMobile ? '#f5f5f5' : '#f8f8f8'};
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }

  &:hover .screenshot-overlay {
    opacity: 1;
  }
`;

const Screenshot = styled.img`
  height: ${props => props.isMobile ? '500px' : '220px'};
  width: ${props => props.isMobile ? 'auto' : '100%'};
  display: block;
  object-fit: contain;
  padding: ${props => props.isMobile ? '0.5rem' : '1rem'};
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    ${props => props.isMobile && `
      width: 100%;
      height: 450px;
      padding: 0.25rem;
    `}
  }
`;

const ZoomOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-size: 0.875rem;
  font-weight: 500;
  pointer-events: none;

  svg {
    font-size: 1.5rem;
  }
`;

const SnapshotsSubtitle = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-top: -0.5rem;
  margin-bottom: 1.5rem;
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.isSimulate ? '#28a745' : '#333'};
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isSimulate ? '#218838' : '#000'};
    transform: translateX(5px);
  }
`;

const SnapshotsTitle = styled.h2`
  font-size: 2rem;
  margin: 3rem 0 1rem 0;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const FullSizeImage = styled.img`
  max-width: 90%;
  max-height: 90vh;
  object-fit: contain;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  color: #2c3e50;
  padding: 0.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #007bff;
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    background-color: rgba(0, 0, 0, 0.7);

    &.left {
      left: 5px;
    }

    &.right {
      right: 5px;
    }
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.left {
    left: 0;
  }

  &.right {
    right: 0;
  }
`;

function ProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();  
    const project = projects.find(p => p.id === parseInt(id));
    const [selectedImage, setSelectedImage] = useState(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);  // Empty dependency array means this runs once when component mounts

    const scroll = (direction) => {
      if (scrollContainerRef.current) {
        const scrollAmount = direction === 'left' ? -400 : 400;
        scrollContainerRef.current.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    };
  
    if (!project) {
      return <div>Project not found</div>;
    }
  
    return (
      <>
        <DetailContainer>
          <BackButton onClick={() => navigate('/projects')}>
            <FaArrowLeft /> Back
          </BackButton>
          <Header>
            <HeaderImage src={project.thumbnail} alt={project.title} />
            <HeaderInfo>
              <Title>{project.title}</Title>
              <Technologies>
                {project.technologies.map((tech, index) => (
                  <span key={index}>{tech}</span>
                ))}
              </Technologies>
            </HeaderInfo>
          </Header>
          <Description>{project.detailedDescription}</Description>
          <ActionLink 
            href={project.projectLink} 
            target="_blank" 
            rel="noopener noreferrer"
            isSimulate={project.id === 1}
          >
            {project.id === 1 ? (
              <>
                <FaPlay /> Simulate
              </>
            ) : project.projectLink.includes('github.com') ? (
              <>
                <FaGithub /> View Source
              </>
            ) : (
              <>
                <FaExternalLinkAlt /> Visit Website
              </>
            )}
          </ActionLink>
          <SnapshotsTitle>Snapshots</SnapshotsTitle>
          <SnapshotsSubtitle>Click any snapshot to enlarge</SnapshotsSubtitle>
          {project.mobileScreenshots ? (
            <MobileScreenshotsContainer>
              <ScrollButton 
                className="left" 
                onClick={() => scroll('left')}
                aria-label="Scroll left"
              >
                <FaChevronLeft />
              </ScrollButton>
              <ScrollButton 
                className="right" 
                onClick={() => scroll('right')}
                aria-label="Scroll right"
              >
                <FaChevronRight />
              </ScrollButton>
              <MobileScreenshots ref={scrollContainerRef}>
                {project.mobileScreenshots.map((screenshot, index) => (
                  <ScreenshotCard
                    key={index}
                    isMobile={true}
                    onClick={() => setSelectedImage(screenshot)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedImage(screenshot); }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Enlarge screenshot ${index + 1}`}
                  >
                    <Screenshot 
                      src={screenshot} 
                      alt={`Screenshot ${index + 1}`}
                      isMobile={true}
                    />
                    <ZoomOverlay className="screenshot-overlay">
                      <FaSearchPlus aria-hidden="true" />
                      <span>Click to enlarge</span>
                    </ZoomOverlay>
                  </ScreenshotCard>
                ))}
              </MobileScreenshots>
            </MobileScreenshotsContainer>
          ) : (
            <Screenshots>
              {project.screenshots.map((screenshot, index) => (
                <ScreenshotCard
                  key={index}
                  isMobile={false}
                  onClick={() => setSelectedImage(screenshot)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedImage(screenshot); }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Enlarge screenshot ${index + 1}`}
                >
                  <Screenshot 
                    src={screenshot} 
                    alt={`Screenshot ${index + 1}`}
                    isMobile={false}
                  />
                  <ZoomOverlay className="screenshot-overlay">
                    <FaSearchPlus aria-hidden="true" />
                    <span>Click to enlarge</span>
                  </ZoomOverlay>
                </ScreenshotCard>
              ))}
            </Screenshots>
          )}
        </DetailContainer>

        {selectedImage && (
          <Modal onClick={() => setSelectedImage(null)}>
            <FullSizeImage 
              src={selectedImage} 
              alt="Full size screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </Modal>
        )}
      </>
    );
}

export default ProjectDetail;
